import React from 'react';
import { motion } from 'framer-motion';
import { getDayDiff } from '../utils/dateUtils';
import { useTheme } from '../context/ThemeContext';
import { useLocale } from '../context/LocaleContext';
import { getThemeStyle } from '../utils/themeUtils';
import SafeIcon from '../common/SafeIcon';

export default function CuteHeroCard({ item, onClick, onShare }) {
  const { isDark } = useTheme();
  const { t } = useLocale();

  const { diffDays, status, weekDayIndex } = getDayDiff(new Date(), item.targetDateISO);
  const isFuture = status === 'remaining' || status === 'today';
  const currentStyle = getThemeStyle(item.theme, status, isDark);

  const handleShare = (e) => {
    e.stopPropagation();
    onShare(item);
  };

  // 動態決定標籤
  const badgeText = item.isPinned 
    ? t('home.hero.pinned') 
    : (isFuture ? t('home.hero.coming') : t('home.hero.recent'));

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onClick(item)}
      className={`relative cursor-pointer rounded-[2.5rem] overflow-hidden shadow-2xl bg-gradient-to-br ${currentStyle.bg} border-8 ${currentStyle.border} p-6 sm:p-10`}
    >
      <div className="absolute -top-20 -right-20 w-64 h-64 bg-white opacity-20 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white opacity-30 rounded-full blur-2xl pointer-events-none"></div>
      
      <button 
        onClick={handleShare}
        className="absolute top-6 right-6 p-3 rounded-full bg-white/20 hover:bg-white/40 text-black/60 dark:text-white/80 backdrop-blur-md transition-all z-20"
      >
        <SafeIcon name="Share2" className="w-5 h-5" />
      </button>

      <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6 pr-10">
        <div className={`flex-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          <div className="inline-block px-3 py-1 bg-white/30 dark:bg-black/20 backdrop-blur-md rounded-full text-sm font-bold mb-4 shadow-sm">
            {badgeText}
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-black mb-2 drop-shadow-sm flex flex-wrap items-center gap-x-3 gap-y-2 break-words">
            <span>{item.icon}</span>
            <span>{t(item.title)}</span>
            {item.isDemo && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl bg-white/50 dark:bg-black/30 backdrop-blur-md border border-white/40 dark:border-white/10 text-pink-600 dark:text-pink-300 text-xs font-black tracking-widest shadow-sm align-middle">
                ✨ {t('common.demoBadge')}
              </span>
            )}
          </h2>
          
          <p className="opacity-90 text-lg font-medium drop-shadow-sm">
            {t('home.hero.target')}{item.targetDateISO} {t(`date.weekdays.${weekDayIndex}`)}
          </p>
          
          {item.note && (
            <p className="mt-3 bg-white/40 dark:bg-black/30 inline-block px-4 py-2 rounded-2xl backdrop-blur-sm font-medium">
              {t(item.note)}
            </p>
          )}
        </div>
        
        <div className="bg-white dark:bg-slate-800 rounded-[2rem] p-6 sm:p-8 flex flex-col items-center justify-center shadow-lg min-w-[160px] text-center transform rotate-2 hover:rotate-0 transition-transform">
          <span className="text-gray-500 dark:text-gray-400 font-bold mb-1">
            {status === 'today' ? t('common.today') : (isFuture ? t('common.remaining') : t('common.elapsed'))}
          </span>
          {status !== 'today' ? (
            <div className={`flex items-baseline gap-1 ${currentStyle.text}`}>
              <span className="text-6xl font-black">{diffDays}</span>
              <span className="text-xl font-bold">{t('common.days')}</span>
            </div>
          ) : (
            <span className="text-5xl font-black">🎉</span>
          )}
        </div>
      </div>
    </motion.div>
  );
}